import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader.js";
import { ChromaticAberrationShader } from "./ChromaticAberrationShader";
import type { RendererService } from "./RendererService";
import type { SceneContext } from "./SceneContext";
import type { QualitySettings, QualityTier } from "../perf/QualityManager";

const CA_MAX_STRENGTH = 0.05;
const CA_DISABLE_THRESHOLD = 1e-5;

export class PostFXPipeline {
  private composer: EffectComposer | null = null;
  private renderPass: RenderPass | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private caPass: ShaderPass | null = null;
  private fxaaPass: ShaderPass | null = null;
  private renderer: RendererService;
  private sceneCtx: SceneContext;
  private tier: QualityTier;
  private bloomEnabled: boolean;
  private bloomScale: number;
  private caEnabledByTier: boolean;

  constructor(renderer: RendererService, sceneCtx: SceneContext, quality: QualitySettings) {
    this.renderer = renderer;
    this.sceneCtx = sceneCtx;
    this.tier = quality.tier;
    this.bloomEnabled = quality.bloomEnabled;
    this.bloomScale = quality.bloomResolutionScale;
    this.caEnabledByTier = quality.tier !== "low";

    this.build();
    renderer.onResize.on(({ width, height }) => this.handleResize(width, height));
  }

  private build(): void {
    const { width, height } = this.renderer.getSize();
    const composer = new EffectComposer(this.renderer.renderer);
    composer.setSize(width, height);

    this.renderPass = new RenderPass(this.sceneCtx.scene, this.sceneCtx.camera);
    composer.addPass(this.renderPass);

    if (this.bloomEnabled) {
      const bloomW = Math.max(2, Math.floor(width * this.bloomScale));
      const bloomH = Math.max(2, Math.floor(height * this.bloomScale));
      this.bloomPass = new UnrealBloomPass(
        new THREE.Vector2(bloomW, bloomH),
        0.7,
        0.6,
        0.2
      );
      composer.addPass(this.bloomPass);
    }

    if (this.caEnabledByTier) {
      this.caPass = new ShaderPass(ChromaticAberrationShader);
      this.caPass.enabled = false; // off until setChromaticAberration() turns it on
      composer.addPass(this.caPass);
    }

    if (this.tier !== "low") {
      this.fxaaPass = new ShaderPass(FXAAShader);
      this.updateFxaaUniforms(width, height);
      composer.addPass(this.fxaaPass);
    }

    this.composer = composer;
  }

  private updateFxaaUniforms(w: number, h: number): void {
    if (!this.fxaaPass) return;
    const pr = this.renderer.renderer.getPixelRatio();
    this.fxaaPass.material.uniforms["resolution"].value.set(1 / (w * pr), 1 / (h * pr));
  }

  private handleResize(w: number, h: number): void {
    if (!this.composer) return;
    this.composer.setSize(w, h);
    if (this.bloomPass) {
      const bloomW = Math.max(2, Math.floor(w * this.bloomScale));
      const bloomH = Math.max(2, Math.floor(h * this.bloomScale));
      this.bloomPass.setSize(bloomW, bloomH);
    }
    this.updateFxaaUniforms(w, h);
  }

  setBloomStrength(strength: number): void {
    if (this.bloomPass) this.bloomPass.strength = strength;
  }

  setBloomThreshold(t: number): void {
    if (this.bloomPass) this.bloomPass.threshold = t;
  }

  setBloomRadius(r: number): void {
    if (this.bloomPass) this.bloomPass.radius = r;
  }

  setChromaticAberration(strength: number): void {
    if (!this.caPass) return;
    const s = Math.max(0, Math.min(CA_MAX_STRENGTH, strength));
    this.caPass.uniforms["strength"]!.value = s;
    // Skip the pass entirely when effectively zero so the GPU doesn't
    // pay for a full-screen 3-tap sample on every frame.
    this.caPass.enabled = s > CA_DISABLE_THRESHOLD;
  }

  render(): void {
    if (this.composer) {
      this.composer.render();
    } else {
      this.renderer.renderer.render(this.sceneCtx.scene, this.sceneCtx.camera);
    }
  }

  dispose(): void {
    this.bloomPass?.dispose?.();
    this.composer?.dispose?.();
    this.composer = null;
    this.bloomPass = null;
    this.caPass = null;
    this.fxaaPass = null;
    this.renderPass = null;
  }
}
